import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarDays, ChevronRight, Inbox, Plus, Search } from 'lucide-react';
import { inquiriesAPI } from '../../services/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { PaginationControls } from '../../components/PaginationControls';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import { BulkActionBar } from '../../components/bulk/BulkActionBar';
import { INQUIRY_STATUSES, STATUS_STYLES, statusLabel } from './constants';

const INQUIRY_CSV_COLUMNS = [
  { header: 'Inquiry No', value: (row) => row.inquiry_number },
  { header: 'Customer', value: (row) => row.customer_name },
  { header: 'Customer Ref', value: (row) => row.customer_inquiry_number },
  { header: 'Items', value: (row) => row.products?.length || 0 },
  { header: 'Status', value: (row) => row.status },
  { header: 'Received', value: (row) => row.customer_inquiry_date || row.created_at },
];

export default function InquiryRegisterPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const selection = useBulkSelection(items);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await inquiriesAPI.list({
        search: search || undefined,
        status: status === 'all' ? undefined : status,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page,
        limit,
      });
      const rows = response.data.items || [];
      setItems(rows);
      selection.remember(rows);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, status, startDate, endDate, page, limit]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [search, status, startDate, endDate, limit]);

  return (
    <div className="space-y-8 animate-fade-in" data-testid="inquiry-register-page">
      <header className="flex flex-col gap-5 border-b pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-blue-700">
            <Inbox className="h-4 w-4" /> Commercial pipeline
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold" data-testid="inquiry-register-heading">
            Inquiries
          </h1>
          <p className="text-muted-foreground" data-testid="inquiry-register-total">{total} retained customer inquiries</p>
        </div>
        <Button onClick={() => navigate('/inquiries/new')} data-testid="new-inquiry-button">
          <Plus className="mr-2 h-4 w-4" /> New inquiry
        </Button>
      </header>

      <section className="grid gap-3 border bg-white p-4 md:grid-cols-[minmax(240px,1fr)_200px_170px_170px]" data-testid="inquiry-filter-bar">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9"
            placeholder="Inquiry, customer, part or quote number" data-testid="inquiry-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="inquiry-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {INQUIRY_STATUSES.map((item) => <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <CalendarDays className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} className="pl-9" data-testid="inquiry-start-date-filter" />
        </div>
        <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} data-testid="inquiry-end-date-filter" />
      </section>

      <BulkActionBar
        entity="inquiries"
        selectedIds={selection.selectedIds}
        selectedRows={selection.selectedRows}
        onClear={selection.clear}
        onDone={load}
        csvColumns={INQUIRY_CSV_COLUMNS}
        csvPrefix="inquiries"
      />

      {selection.selectedIds.size === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="inquiry-bulk-hint">
          Tip: tick one or more inquiries to change their status in bulk (for example to <strong>Quoted</strong> when the
          quotation was made outside the tool and only uploaded to Documents), export them, or delete them.
        </p>
      )}

      <section className="overflow-hidden border bg-white" data-testid="inquiry-register-table">
        {loading ? (
          <div className="space-y-3 p-5" data-testid="inquiry-register-loading">
            {[1, 2, 3, 4].map((row) => <Skeleton key={row} className="h-12 w-full" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="py-20 text-center" data-testid="inquiry-empty-state">
            <Inbox className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
            <p className="font-medium">No inquiries match these filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={selection.allOnPageSelected}
                    onCheckedChange={selection.togglePage}
                    aria-label="Select all inquiries on this page"
                    data-testid="inquiries-select-all-page"
                  />
                </TableHead>
                <TableHead>Inquiry</TableHead><TableHead>Customer</TableHead><TableHead>Customer ref.</TableHead>
                <TableHead>Items</TableHead><TableHead>Status</TableHead><TableHead>Owner</TableHead><TableHead>Received</TableHead><TableHead className="w-14" />
              </TableRow></TableHeader>
              <TableBody>{items.map((inquiry) => (
                <TableRow key={inquiry.id} className="cursor-pointer hover:bg-slate-50"
                  data-state={selection.isSelected(inquiry.id) ? 'selected' : undefined}
                  data-testid={`inquiry-row-${inquiry.id}`}>
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                      checked={selection.isSelected(inquiry.id)}
                      onCheckedChange={() => selection.toggleOne(inquiry)}
                      aria-label={`Select inquiry ${inquiry.inquiry_number}`}
                      data-testid={`inquiries-row-checkbox-${inquiry.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono font-semibold text-blue-700" onClick={() => navigate(`/inquiries/${inquiry.id}`)} data-testid={`inquiry-number-${inquiry.id}`}>{inquiry.inquiry_number}</TableCell>
                  <TableCell className="font-medium" onClick={() => navigate(`/inquiries/${inquiry.id}`)} data-testid={`inquiry-customer-${inquiry.id}`}>{inquiry.customer_name}</TableCell>
                  <TableCell onClick={() => navigate(`/inquiries/${inquiry.id}`)}>{inquiry.customer_inquiry_number || '—'}</TableCell>
                  <TableCell onClick={() => navigate(`/inquiries/${inquiry.id}`)}>{inquiry.products?.length || 0}</TableCell>
                  <TableCell onClick={() => navigate(`/inquiries/${inquiry.id}`)}><Badge className={STATUS_STYLES[inquiry.status]} data-testid={`inquiry-status-${inquiry.id}`}>{statusLabel(inquiry.status)}</Badge></TableCell>
                  <TableCell onClick={() => navigate(`/inquiries/${inquiry.id}`)} data-testid={`inquiry-owner-${inquiry.id}`}>
                    {inquiry.owner_name || <span className="text-muted-foreground">Unassigned</span>}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/inquiries/${inquiry.id}`)}>{new Date(inquiry.customer_inquiry_date || inquiry.created_at).toLocaleDateString('en-IN')}</TableCell>
                  <TableCell onClick={() => navigate(`/inquiries/${inquiry.id}`)}><ChevronRight className="h-4 w-4 text-muted-foreground" /></TableCell>
                </TableRow>
              ))}</TableBody>
            </Table>
          </div>
        )}
        <PaginationControls
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onLimitChange={setLimit}
          onPageChange={setPage}
        />
      </section>
    </div>
  );
}
